import { Injectable } from '@angular/core';
import { endpoint } from '@tekman/onmat/constants';
import { ProgressActivityModel, Session, SessionEvaluationInstrument } from '@tekman/onmat/models';
import { ApiService } from '@tekman/onmat/service/generic';
import { Observable, of, timer } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/internal/operators';
import { catchError } from 'rxjs/operators';
import { AlertService } from '@tekman/shared/service';

@Injectable({
  providedIn: 'root',
})
export class StepsService {
  constructor(private readonly apiService: ApiService, private alertService: AlertService) {}

  getStep(itineraryId: string, stepId: string, groupId: string): Observable<Session[]> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.itineraries.step.retrieve, '{itineraryId}', itineraryId),
        '{groupId}',
        groupId
      ),
      '{stepId}',
      stepId
    );
    return this.apiService.get<Session[]>(url);
  }

  updateStep(itineraryId: string, stepId: string, groupId: string, sessions: Session[]) {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(this.apiService.replaceUrl(endpoint.itineraries.step.retrieve, '{stepId}', stepId), '{groupId}', groupId),
      '{itineraryId}',
      itineraryId
    );
    return this.apiService.post(url, sessions);
  }

  getEvaluationInstruments(stepId: string, groupId: string): Observable<SessionEvaluationInstrument[]> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.itineraries.step.evaluationItems, '{stepId}', stepId),
      '{groupId}',
      groupId
    );
    return this.apiService.get<SessionEvaluationInstrument[]>(url);
  }

  getOtherActivities(itineraryId: string, stepId: string, groupId: string): Observable<SessionEvaluationInstrument[]> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.itineraries.step.otherActivities, '{stepId}', stepId),
        '{groupId}',
        groupId
      ),
      '{itineraryId}',
      itineraryId
    );
    return this.apiService.get<SessionEvaluationInstrument[]>(url);
  }

  rollbackStep(itineraryId: string, stepId: string, groupId: string) {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(this.apiService.replaceUrl(endpoint.itineraries.step.retrieve, '{stepId}', stepId), '{groupId}', groupId),
      '{itineraryId}',
      itineraryId
    );
    return this.apiService.delete(url);
  }

  createQualificationsFile(groupId: string, stepId: string): Observable<Blob | undefined> {
    const url = this.apiService.replaceUrlArray(endpoint.itineraries.step.qualifications.create, ['{id}', '{stepId}'], [groupId, stepId]);
    return this.apiService.post(url, {}).pipe(
      switchMap(() => {
        return timer(0, 5000).pipe(
          switchMap(() => this.statusQualificationsFile(groupId, stepId)),
          takeWhile(status => !status, true) // Detener cuando el archivo esté listo
        );
      }),
      switchMap(() => this.downloadQualificationsFile(groupId, stepId)),
      catchError(() => {
        return of(undefined);
      })
    );
  }

  private statusQualificationsFile(groupId: string, stepId: string) {
    this.alertService.openAlert('', true, 5000, true);
    const url = this.apiService.replaceUrlArray(endpoint.itineraries.step.qualifications.status, ['{id}', '{stepId}'], [groupId, stepId]);
    return this.apiService.get(url);
  }

  private downloadQualificationsFile(groupId: string, stepId: string): Observable<Blob> {
    const url = this.apiService.replaceUrlArray(endpoint.itineraries.step.qualifications.download, ['{id}', '{stepId}'], [groupId, stepId]);
    return this.apiService.getFile(url);
  }

  getProgress(activityId: string, groupId: string, studentId: string): Observable<ProgressActivityModel> {
    return this.apiService.get<ProgressActivityModel>(
      this.apiService.replaceUrlArray(
        endpoint.activity.progress.get,
        ['{activityId}', '{groupId}', '{studentId}'],
        [activityId, groupId, studentId]
      )
    );
  }
}
